// MyContext.jsx — propertyInputs + projectInputs dynamic from backend
// ✅ range fields, documents (layout/brochure), company_profile support added

import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getStoredLocale, setStoredLocale, hasUserSetLocale, withLocale } from '../utils/localeStorage';
import { mergeContentTranslations } from '../utils/applyContentTranslations';
import API, { BASE_URL } from '../Api/api';
import { clearInvalidUserSession, isValidAuthToken } from '../utils/authSession';
import { getAuthToken } from '../Api/authToken';
import { startPresence } from '../Services/presenceService';

import _aminities           from '../json/aminities';
import _aminitiesCategories from '../json/aminitiesCategories';
import _bookingform         from '../json/bookingform';
import _categorys           from '../json/categorys';
import _cities              from '../json/cities';
import _contact             from '../json/contact';
import _dropdown            from '../json/dropdown';
import _filterConfig        from '../json/filterConfig';
import _footerData          from '../json/footerData';
import { applyNearLocationConfig, NEAR_LOCATION_DEFAULTS } from '../utils/nearLocationConfig';
import _heroData            from '../json/heroData';
import _homeSections        from '../json/homeSections';
import _landtype            from '../json/landtype';
import _listingTypes        from '../json/listingTypes';
import _logininput          from '../json/logininput';
import _navLinks            from '../json/navLinks';
import _nearestlocation     from '../json/nearestlocation';
import _partners            from '../json/partners';
import _roles               from '../json/roles';
import _privacy             from '../json/privacy';
import _projectBlocks       from '../json/projectBlocks';
import _projectInputs       from '../json/projectInputs';
import _propertyBlocks      from '../json/propertyBlocks';
import _propertydetail      from '../json/propertydetail';
import _propertyInputs      from '../json/propertyInputs';
import _users               from '../json/users';
import _termsData           from '../json/terms';
import _states              from '../json/states';
import _subregisters        from '../json/subregisters';
import _whyChooseData       from '../json/whyChooseData';
import _youtubeShorts       from '../json/youtubeShorts';
import _admins              from '../json/admins';
import _socialmedia         from '../json/socialmedia';
import {
  PROJECT_TYPE_SHOW as _PROJECT_TYPE_SHOW,
  propertyTypes     as _propertyTypes,
  projectTypes      as _projectTypes,
} from '../json/types';
import { saveCustomDropdownsToExtraFields } from '../utils/dropdownCustomValue';
import { trackActivity, propertyActivityLabel } from '../utils/userActivity';
import { forceUserLogout, isAccountDeactivatedError } from '../utils/accountGuard';

export const MyContext = createContext();

// ── Image URL helper ──────────────────────────────────────────
export const getImgUrl = (path) => {
  if (!path) return null;
  if (
    path.startsWith('http') || path.startsWith('https') ||
    path.startsWith('blob:') || path.startsWith('data:')
  ) return path;
  return `${BASE_URL}/${path.replace(/^\/+/, '')}`;
};

/** Normalize stored upload path (filename or uploads/...) for getImgUrl */
export const uploadsPublicPath = (stored) => {
  if (!stored) return '';
  const s = String(stored).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const p = s.replace(/^\/+/, '');
  return p.startsWith('uploads/') ? p : `uploads/${p}`;
};

/** Resolve hero bg path — bundled static assets, URLs, or upload filenames */
export const normalizeHeroBgImage = (img) => {
  if (!img) return null;
  const s = String(img);
  if (
    s.startsWith('http') || s.startsWith('https') ||
    s.startsWith('data:') || s.startsWith('blob:') ||
    s.startsWith('/static/')
  ) return s;
  return getImgUrl(uploadsPublicPath(s));
};

export const applyHeroData = (data, fallback = {}) => {
  const merged = { ...fallback, ...(data && typeof data === 'object' ? data : {}) };
  const apiBgs = Array.isArray(merged.bg_images)
    ? merged.bg_images.map(normalizeHeroBgImage).filter(Boolean)
    : [];
  const fallbackBgs = Array.isArray(fallback.bg_images)
    ? fallback.bg_images.map(normalizeHeroBgImage).filter(Boolean)
    : [];
  return {
    ...merged,
    bg_images: apiBgs.length ? apiBgs : fallbackBgs,
    nearby_properties_enabled: merged.nearby_properties_enabled !== false,
    nearby_radius_km: (() => {
      const n = Number(merged.nearby_radius_km);
      if (!Number.isFinite(n) || n < 1) return Number(fallback.nearby_radius_km) || 10;
      return n;
    })(),
  };
};

const normalizeCategory = (category) => {
  // category_id is the auto-increment field; prefer it over legacy id values.
  const resolvedId = Number(category?.category_id ?? category?.id ?? category?.categoryId);
  const parsedTypeId = Array.isArray(category?.type_id)
    ? category.type_id.map((v) => Number(v)).filter((n) => !Number.isNaN(n))
    : [];
  return {
    ...category,
    id: Number.isNaN(resolvedId) ? category?.id : resolvedId,
    category_id: Number.isNaN(resolvedId) ? category?.category_id : resolvedId,
    type_id: parsedTypeId,
    is_active: category?.is_active !== false
  };
};

const resolveEntityId = (entity, ...keys) => {
  if (!entity) return null;
  for (const key of keys) {
    const n = Number(entity[key]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

// ── Convert array → object keyed by field ────────────────────
const arrToObj = (arr) => {
  if (!arr) return {};
  if (!Array.isArray(arr)) return arr;
  const obj = {};
  arr.forEach(f => { if (f?.field) obj[f.field] = f; });
  return obj;
};

// ── Number helper ─────────────────────────────────────────────
const toNum = (v) => {
  if (v === null || v === undefined || v === '' || v === false) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

// ── Upload helpers ────────────────────────────────────────────
const uploadFile = async (file) => {
  if (!file || !(file instanceof File)) return null;
  const form = new FormData();
  form.append('file', file);
  const res = await API.post('/upload/single', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.path;
};

const uploadFiles = async (files) => {
  const arr      = Array.isArray(files) ? files : [files];
  const fileObjs = arr.filter(f => f instanceof File);
  const existing = arr.filter(f => typeof f === 'string' && f);
  if (!fileObjs.length) return existing;
  const form = new FormData();
  fileObjs.forEach(f => form.append('files', f));
  const res = await API.post('/upload/multiple', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return [...existing, ...(res.data.paths || [])];
};

// ✅ Document upload — image array or single PDF
const uploadDocument = async (val, docType) => {
  if (!val) return docType === 'image' ? [] : null;
  if (docType === 'pdf') {
    if (val instanceof File) return await uploadFile(val);
    if (typeof val === 'string' && val) return val;
    return null;
  }
  // image array
  if (Array.isArray(val)) {
    const fileObjs = val.filter(f => f instanceof File);
    const existing = val.filter(f => typeof f === 'string' && f);
    if (!fileObjs.length) return existing;
    const form = new FormData();
    fileObjs.forEach(f => form.append('files', f));
    const res = await API.post('/upload/multiple', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return [...existing, ...(res.data.paths || [])];
  }
  return [];
};

// ─────────────────────────────────────────────────────────────
export const MyProvider = ({ children }) => {
  const DEFAULT_PROPERTY_LIST_PAGE_CONFIG = useMemo(() => ({
    options: [20, 50, 100],
    default: 20,
    allowed: [20, 50, 100],
  }), []);


  const [loading, setLoading] = useState(false);
  const [locale, setLocaleState] = useState(() => getStoredLocale());
  const [localizationEnabled, setLocalizationEnabled] = useState(true);
  const [appDefaultLocale, setAppDefaultLocale] = useState('en');

  const changeLocale = useCallback(async (nextLocale) => {
    if (!localizationEnabled) {
      return appDefaultLocale;
    }
    const code = String(nextLocale || 'en').toLowerCase();
    setStoredLocale(code, true);
    setLocaleState(code);
    try {
      const token = getAuthToken();
      if (token) {
        const res = await API.put('/auth/profile', { preferred_locale: code });
        if (res.data?.id) {
          setUser((prev) => {
            const merged = { ...(prev || {}), ...res.data, preferred_locale: code };
            localStorage.setItem('loggedUser', JSON.stringify(merged));
            return merged;
          });
        }
      }
    } catch (_) { /* guest or offline */ }
    return code;
  }, [appDefaultLocale, localizationEnabled]);

  const [searchParams, setSearchParams] = useSearchParams();

  // ── Auth ──────────────────────────────────────────────────
  const [user,   setUser]   = useState(null);
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    if (!user?.preferred_locale || hasUserSetLocale()) return;
    if (user.preferred_locale !== locale) {
      setStoredLocale(user.preferred_locale);
      setLocaleState(user.preferred_locale);
    }
  }, [user?.id, user?.preferred_locale]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearInvalidUserSession();
    const token = localStorage.getItem('user_access_token');
    const raw   = localStorage.getItem('loggedUser');
    const onUserSite =
      !window.location.pathname.startsWith('/admin') &&
      !window.location.pathname.startsWith('/partner');
    if (isValidAuthToken(token) && raw) {
      try {
        setUser(JSON.parse(raw));
        setIsAuth(true);
        if (onUserSite) startPresence();
      } catch {}
      API.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          if (r.data?.id) {
            if (r.data.is_active === false) {
              forceUserLogout();
              return;
            }
            const fresh = {
              ...r.data,
              access_token:  token,
              refersh_token: localStorage.getItem('user_refresh_token') || '',
            };
            setUser(fresh);
            setWishlist(fresh.wishlist || []);
            localStorage.setItem('loggedUser', JSON.stringify(fresh));
            if (onUserSite) startPresence();
          }
        }).catch((err) => {
          if (isAccountDeactivatedError(err)) forceUserLogout();
        });
    }
  }, []);

  // Poll session — auto logout deactivated users mid-session
  useEffect(() => {
    if (!user?.id) return undefined;
    const token = localStorage.getItem('user_access_token');
    if (!isValidAuthToken(token)) return undefined;

    const verifyActive = () => {
      API.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          if (r.data?.is_active === false) forceUserLogout();
        })
        .catch((err) => {
          if (isAccountDeactivatedError(err)) forceUserLogout();
        });
    };

    verifyActive();
    const interval = setInterval(verifyActive, 60000);
    const onFocus = () => verifyActive();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.id]);

  useEffect(() => { setIsAuth(!!user); }, [user]);

  const [wishlist, setWishlist] = useState(() => {
    try {
      const s = localStorage.getItem('loggedUser');
      return s ? (JSON.parse(s).wishlist || []) : [];
    } catch { return []; }
  });

  // ── City Selection ──────────────────────────────────────────
  const [selectedCity, setSelectedCity] = useState(() => {
    try {
      const s = localStorage.getItem('selected_city');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });

  const [selectedPropertyType, setSelectedPropertyType] = useState(() => {
    try {
      const s = localStorage.getItem('user_preferences');
      const p = s ? JSON.parse(s) : null;
      return p?.type || null;
    } catch { return null; }
  });

  const [selectedCategory, setSelectedCategory] = useState(() => {
    try {
      const s = localStorage.getItem('user_preferences');
      const p = s ? JSON.parse(s) : null;
      return p?.category || null;
    } catch { return null; }
  });

  const [selectedState, setSelectedState] = useState(() => {
    try {
      const s = localStorage.getItem('user_preferences');
      const p = s ? JSON.parse(s) : null;
      return p?.state || null;
    } catch { return null; }
  });

  const preferencesComplete = !!(
    resolveEntityId(selectedPropertyType, 'id')
    && resolveEntityId(selectedCategory, 'category_id', 'id')
    && resolveEntityId(selectedCity, 'id')
    && (resolveEntityId(selectedState, 'id') || resolveEntityId(selectedCity, 'state_id'))
  );

  const changeCity = useCallback((city) => {
    setSelectedCity(city);
    if (city) localStorage.setItem('selected_city', JSON.stringify(city));
    else localStorage.removeItem('selected_city');
  }, []);

  const savePreferences = useCallback(async ({ type, category, state, city }) => {
    setSelectedPropertyType(type || null);
    setSelectedCategory(category || null);
    setSelectedState(state || null);
    changeCity(city || null);
    if (type && category && city) {
      localStorage.setItem('user_preferences', JSON.stringify({
        type, category, state: state || null, city,
      }));
    } else {
      localStorage.removeItem('user_preferences');
    }

    const token = localStorage.getItem('user_access_token') || localStorage.getItem('access_token');
    if (!token) return null;

    const payload = {
      selected_type_id: resolveEntityId(type, 'id'),
      selected_category_id: resolveEntityId(category, 'category_id', 'id'),
      selected_city_id: resolveEntityId(city, 'id'),
      selected_state_id: resolveEntityId(state, 'id') ?? resolveEntityId(city, 'state_id'),
      selected_type_name: type?.type_name ?? '',
      selected_category_name: category?.category_name ?? '',
      location: city?.city_name ?? '',
    };

    try {
      const res = await API.put('/auth/profile', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data) {
        const merged = {
          ...(user || {}),
          ...res.data,
          access_token: token,
          refersh_token: localStorage.getItem('user_refresh_token') || user?.refersh_token || '',
        };
        setUser(merged);
        localStorage.setItem('loggedUser', JSON.stringify(merged));
        return res.data;
      }
    } catch (e) {
      console.log('savePreferences:', e.message);
      throw e;
    }
    return null;
  }, [changeCity, setUser, user]);

  const toggleWishlist = useCallback(async (propertyId, meta = {}) => {
    if (!user) return false;
    const isAdding = !wishlist.includes(propertyId);
    const updated = isAdding
      ? [...wishlist, propertyId]
      : wishlist.filter(id => id !== propertyId);
    setWishlist(updated);
    const updatedUser = { ...user, wishlist: updated };
    setUser(updatedUser);
    localStorage.setItem('loggedUser', JSON.stringify(updatedUser));
    try {
      const token = localStorage.getItem('user_access_token');
      if (token)
        await API.put('/auth/profile', { wishlist: updated }, {
          headers: { Authorization: `Bearer ${token}` },
        });
    } catch (e) { console.log('wishlist sync:', e.message); }

    if (isAdding) {
      await trackActivity({
        type: 'wishlist',
        label: meta.label || propertyActivityLabel({ id: propertyId, ...meta }, 'Added to wishlist'),
        url: meta.url || `/property/${propertyId}`,
        property_id: propertyId,
      }, user);
    }
    return true;
  }, [user, wishlist]);

  // ── Properties ────────────────────────────────────────────
  // Properties are NOT pre-loaded into context. Use fetchPropertiesPaged
  // in individual pages so only the current page's data is in memory.
  const [properties,  setProperties]  = useState([]);
  const [propLoading, setPropLoading] = useState(false);

  // Legacy full-fetch — kept for admin/create screens only
  const fetchProperties = useCallback(async () => {
    setPropLoading(true);
    try {
      const r = await API.get('/property/all');
      setProperties(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.log('fetchProperties:', e.message); }
    finally { setPropLoading(false); }
  }, []);

  // ✅ Paginated fetch — used by listing/home pages
  // params: { page, limit, status, q, sort, city_id, category_id, 
  //           listing_type, sales_type, is_urgent, is_highlighted,
  //           is_project, pmin, pmax }
  const fetchPropertiesPaged = useCallback(async (params = {}) => {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')
    ).toString();
    const r = await API.get(`/property${queryString ? '?' + queryString : ''}`);
    // Returns { data, total, page, pages, limit }
    return r.data;
  }, []);

  // ✅ Fetch single property by ID
  const fetchPropertyById = useCallback(async (id) => {
    try {
      const r = await API.get(`/property/${id}`);
      return r.data;
    } catch (e) {
      console.log('fetchPropertyById:', e.message);
      return null;
    }
  }, []);

  // ── Dynamic data states ───────────────────────────────────
  const [aminities,      setAminities]      = useState(Array.isArray(_aminities)      ? _aminities      : []);
  const [aminitiesCategories, setAminitiesCategories] = useState(
    Array.isArray(_aminitiesCategories) ? _aminitiesCategories : [],
  );
  const [nearestlocation,setNearestlocation]= useState(Array.isArray(_nearestlocation)? _nearestlocation: []);
  const [landtype,       setLandtype]       = useState(Array.isArray(_landtype)       ? _landtype       : []);
  const [states,         setStates]         = useState(Array.isArray(_states)         ? _states         : []);
  const [subregisters,   setSubregisters]   = useState(Array.isArray(_subregisters)   ? _subregisters   : []);
  const [listingTypes,   setListingTypes]   = useState(Array.isArray(_listingTypes)   ? _listingTypes   : []);
  const [roles,          setRoles]          = useState(Array.isArray(_roles)          ? _roles          : []);
  const [propertyBlocks, setPropertyBlocks] = useState(Array.isArray(_propertyBlocks) ? _propertyBlocks : []);
  const [projectBlocks,  setProjectBlocks]  = useState(Array.isArray(_projectBlocks)  ? _projectBlocks  : []);
  const [propertydetail, setPropertydetail] = useState(Array.isArray(_propertydetail) ? _propertydetail : []);
  const [logininput,     setLogininput]     = useState(_logininput || {});
  const [colors,         setColors]         = useState(null);
  const [partners,       setPartners]       = useState(Array.isArray(_partners)       ? _partners       : []);
  const [socialMedia,    setSocialMedia]    = useState(Array.isArray(_socialmedia)    ? _socialmedia    : []);
  const [navLinks,       setNavLinks]       = useState(Array.isArray(_navLinks)       ? _navLinks       : []);
  const [privacy,        setPrivacy]        = useState(_privacy   || {});
  const [termsData,      setTermsData]      = useState(_termsData || {});
  const [filterConfig,   setFilterConfig]   = useState(Array.isArray(_filterConfig)   ? _filterConfig   : []);
  const [homeSections,   setHomeSections]   = useState(Array.isArray(_homeSections)   ? _homeSections   : []);
  const [whyChooseData,  setWhyChooseData]  = useState(_whyChooseData || {});
  const [youtubeShorts,  setYoutubeShorts]  = useState(_youtubeShorts || {});
  const [bookingform,    setBookingform]     = useState(Array.isArray(_bookingform)    ? _bookingform    : []);
  const [dropdown,       setDropdown]       = useState(Array.isArray(_dropdown)       ? _dropdown       : []);
  const [adsData,        setAdsData]        = useState([]);
  const [categorys,      setCategorys]      = useState(Array.isArray(_categorys)      ? _categorys      : []);
  const [cities,         setCities]         = useState(Array.isArray(_cities)         ? _cities         : []);

  const [contact,        setContact]        = useState(_contact || {});
  const [footerData, setFooterData] = useState(_footerData || {});
  const [heroData, setHeroData] = useState(_heroData || {});
  const [nearLocationConfig, setNearLocationConfig] = useState(NEAR_LOCATION_DEFAULTS);
  const [propertyListPageConfig, setPropertyListPageConfig] = useState(DEFAULT_PROPERTY_LIST_PAGE_CONFIG);

  const refreshLocalizationSettings = useCallback(async () => {
    try {
      const r = await API.get('/localization/languages');
      const enabled = r.data?.localization_enabled !== false;
      const defaultLoc = r.data?.default_locale || 'en';
      setLocalizationEnabled(enabled);
      setAppDefaultLocale(defaultLoc);
      if (!enabled) {
        setStoredLocale(defaultLoc, false);
        setLocaleState(defaultLoc);
      }
    } catch {
      setLocalizationEnabled(true);
    }
  }, []);

  const refreshNearLocationConfig = useCallback(async () => {
    try {
      const r = await API.get('/near-location-config');
      setNearLocationConfig(applyNearLocationConfig(r.data, heroData));
    } catch {
      setNearLocationConfig(applyNearLocationConfig(null, heroData));
    }
  }, [heroData]);

  const refreshHeroConfig = useCallback(async () => {
    const loc = withLocale(locale);
    try {
      const r = await API.get('/hero', loc);
      let d = r.data;
      const hasData = d && typeof d === 'object' && Object.keys(d).length;
      if (!hasData) {
        d = await mergeContentTranslations(API, 'hero', locale, { ..._heroData });
      } else {
        d = await mergeContentTranslations(API, 'hero', locale, { ..._heroData, ...d });
      }
      const nextHero = applyHeroData(d, _heroData);
      setHeroData(nextHero);
      setNearLocationConfig((prev) => applyNearLocationConfig(prev, nextHero));
    } catch {
      const fallbackHero = applyHeroData(
        await mergeContentTranslations(API, 'hero', locale, { ..._heroData }),
        _heroData,
      );
      setHeroData(fallbackHero);
      setNearLocationConfig((prev) => applyNearLocationConfig(prev, fallbackHero));
    }
  }, [locale]);

  const refreshPropertyListPageConfig = useCallback(async () => {
    try {
      const r = await API.get('/settings/property-list-page-size');
      const raw = r.data || {};
      const options = Array.isArray(raw.options) ? raw.options.map(Number).filter(Number.isFinite) : [];
      const safeOptions = options.length ? options : DEFAULT_PROPERTY_LIST_PAGE_CONFIG.options;
      const requestedDefault = Number(raw.default_page_size ?? raw.defaultSize ?? raw.default);
      setPropertyListPageConfig({
        options: safeOptions,
        default: safeOptions.includes(requestedDefault) ? requestedDefault : safeOptions[0],
        allowed: Array.isArray(raw.allowed) ? raw.allowed.map(Number).filter(Number.isFinite) : DEFAULT_PROPERTY_LIST_PAGE_CONFIG.allowed,
      });
    } catch {
      setPropertyListPageConfig(DEFAULT_PROPERTY_LIST_PAGE_CONFIG);
    }
  }, [DEFAULT_PROPERTY_LIST_PAGE_CONFIG]);

  useEffect(() => {
    refreshNearLocationConfig();
    refreshLocalizationSettings();
    refreshPropertyListPageConfig();
  }, [refreshNearLocationConfig, refreshLocalizationSettings, refreshPropertyListPageConfig]);

  useEffect(() => {
    const sec = Number(nearLocationConfig?.config_refresh_interval_sec) || 45;
    const id = setInterval(() => {
      refreshNearLocationConfig();
      refreshHeroConfig();
      refreshLocalizationSettings();
      refreshPropertyListPageConfig();
    }, Math.max(15000, sec * 1000));
    return () => clearInterval(id);
  }, [nearLocationConfig?.config_refresh_interval_sec, refreshNearLocationConfig, refreshHeroConfig, refreshLocalizationSettings, refreshPropertyListPageConfig]);

  const [admins,         setAdmins]         = useState(Array.isArray(_admins)         ? _admins         : []);

  // ✅ Inputs — object keyed by field
  const [propertyInputs,  setPropertyInputs]  = useState({});
  const [projectInputs,   setProjectInputs]   = useState(arrToObj(_projectInputs));

  // ✅ Types + PROJECT_TYPE_SHOW
  const [propertyTypes,   setPropertyTypes]   = useState(_propertyTypes   || []);
  const [projectTypes,    setProjectTypes]    = useState(_projectTypes    || []);
  const [projectTypeShow, setProjectTypeShow] = useState(_PROJECT_TYPE_SHOW ?? true);

  // Restore user preferences from profile when lookup data is loaded
  useEffect(() => {
    if (!user || !cities?.length) return;
    if (user.selected_city_id) {
      const city = cities.find((c) => Number(c.id) === Number(user.selected_city_id));
      if (city) {
        setSelectedCity(city);
        localStorage.setItem('selected_city', JSON.stringify(city));
      }
    }
    if (user.selected_state_id && states?.length) {
      const state = states.find((s) => Number(s.id) === Number(user.selected_state_id));
      if (state) setSelectedState(state);
    }
    if (user.selected_type_id && propertyTypes?.length) {
      const type = propertyTypes.find((t) => Number(t.id) === Number(user.selected_type_id));
      if (type) setSelectedPropertyType(type);
    }
    if (user.selected_category_id && categorys?.length) {
      // category_id is the auto-increment field; id is normalized to equal it, but use both as fallback
      const category = categorys.find(
        (c) => Number(c.category_id ?? c.id) === Number(user.selected_category_id)
      );
      if (category) setSelectedCategory(category);
    }
    if (user.selected_type_id && user.selected_category_id && user.selected_city_id) {
      const city = cities.find((c) => Number(c.id) === Number(user.selected_city_id));
      const state = states?.find((s) => Number(s.id) === Number(user.selected_state_id || city?.state_id));
      const type = propertyTypes?.find((t) => Number(t.id) === Number(user.selected_type_id));
      const category = categorys?.find(
        (c) => Number(c.category_id ?? c.id) === Number(user.selected_category_id)
      );
      if (type && category && city) {
        localStorage.setItem('user_preferences', JSON.stringify({
          type, category, state: state || null, city,
        }));
      }
    }
  }, [
    user?.selected_city_id,
    user?.selected_state_id,
    user?.selected_type_id,
    user?.selected_category_id,
    cities,
    states,
    propertyTypes,
    categorys,
  ]);

  // ── Tiered API loading (database content — localized via ?locale=) ──
  useEffect(() => {
    const loc = withLocale(locale);
    const safe = (url, setter, fallback) =>
      API.get(url, loc)
        .then(r => {
          const d = r.data;
          if (Array.isArray(d) && d.length > 0) setter(d);
          else if (d && typeof d === 'object' && !Array.isArray(d) && Object.keys(d).length) setter(d);
          else if (fallback !== undefined) setter(Array.isArray(fallback) ? fallback : fallback);
        })
        .catch(() => { if (fallback !== undefined) setter(Array.isArray(fallback) ? fallback : fallback); });

    const tier1 = async () => {
      await Promise.all([
        API.get('/settings', loc).then(r => {
          (r.data || []).forEach(s => {
            if (s.key === 'project_type_show') setProjectTypeShow(s.value === true || s.value === 'true');
            else if (s.value && typeof s.value === 'object') setColors(s.value);
          });
        }).catch(() => {}),
        API.get('/category', loc).then(r => {
          const d = r.data;
          if (Array.isArray(d) && d.length > 0) {
            setCategorys(d.map(c => ({
              ...normalizeCategory(c),
              category_image_url: c.category_image ? getImgUrl('uploads/' + c.category_image) : null,
            })));
          }
        }).catch(() => {}),
        // Cities/states early — property details & filters need them for complete first paint
        safe('/cities', setCities, _cities),
        safe('/states', setStates, _states),
        API.get('/hero', loc).then(async (r) => {
          let d = r.data;
          const hasData = d && typeof d === 'object' && Object.keys(d).length;
          if (!hasData) {
            d = await mergeContentTranslations(API, 'hero', locale, { ..._heroData });
          } else {
            d = await mergeContentTranslations(API, 'hero', locale, { ..._heroData, ...d });
          }
          setHeroData(applyHeroData(d, _heroData));
        }).catch(async () => {
          setHeroData(applyHeroData(
            await mergeContentTranslations(API, 'hero', locale, { ..._heroData }),
            _heroData,
          ));
        }),
        API.get('/home-sections', loc).then(r => { if (r.data?.length) setHomeSections(r.data); }).catch(() => {}),
        API.get('/contact', loc).then(r => {
          const d = r.data;
          if (d && typeof d === 'object' && Object.keys(d).length) setContact(d);
        }).catch(() => {}),
        API.get('/footer', loc).then(async (r) => {
          let d = r.data;
          const hasData = d && typeof d === 'object' && Object.keys(d).length;
          if (!hasData) {
            d = await mergeContentTranslations(API, 'footer', locale, { ..._footerData });
          }
          setFooterData(d);
        }).catch(async () => {
          setFooterData(await mergeContentTranslations(API, 'footer', locale, { ..._footerData }));
        }),
      ]);
    };

    const tier2 = setTimeout(() => {
      Promise.all([
        safe('/amenities', setAminities, _aminities),
        API.get('/amenities-category', loc).then((r) => {
          const d = r.data;
          if (Array.isArray(d) && d.length > 0) {
            setAminitiesCategories(d.map((c) => ({
              ...c,
              id: Number(c.id ?? c.amenities_category_id ?? c.amenitiesCategoryId),
            })));
          }
        }).catch(() => { setAminitiesCategories(Array.isArray(_aminitiesCategories) ? _aminitiesCategories : []); }),
        safe('/filter', setFilterConfig, _filterConfig),
        safe('/property-detail', setPropertydetail, _propertydetail),
        safe('/nearest-location', setNearestlocation, _nearestlocation),
        safe('/listing-types', setListingTypes, _listingTypes),
        API.get('/nav-links', loc).then(r => { if (r.data?.length) setNavLinks(r.data); }).catch(() => {}),
        API.get('/ads/public', loc).then(r => { if (Array.isArray(r.data) && r.data.length) setAdsData(r.data); else setAdsData([]); }).catch(() => { setAdsData([]); }),
        API.get('/partners').then(r => { if (r.data?.length) setPartners(r.data); }).catch(() => {}),
        API.get('/social-media', loc).then(r => { if (r.data?.length) setSocialMedia(r.data); }).catch(() => {}),
        API.get('/types/property', loc).then(r => { if (r.data?.length) setPropertyTypes(r.data); }).catch(() => {}),
        API.get('/types/project', loc).then(r => { if (r.data?.length) setProjectTypes(r.data); }).catch(() => {}),
        API.get('/dropdown', loc).then(r => { if (r.data?.length) setDropdown(r.data); }).catch(() => {}),
        API.get('/why-choose', loc).then(r => { if (r.data) setWhyChooseData(r.data); }).catch(() => {}),
        API.get('/youtube-shorts', loc).then(r => { if (r.data) setYoutubeShorts(r.data); }).catch(() => {}),
        API.get('/admin/contact-admins').then(r => { if (r.data?.length) setAdmins(r.data); }).catch(() => {}),
      ]);
    }, 200);

    const tier3 = setTimeout(() => {
      safe('/landtype', setLandtype, _landtype);
      safe('/subregisters', setSubregisters, _subregisters);
      safe('/roles', setRoles, _roles);
      API.get('/property-blocks', loc).then(r => {
        if (r.data?.length) {
          const blocks = (Array.isArray(r.data) ? r.data : []).map((b) => ({
            ...b,
            is_property_show: b.is_property_show === true || b.is_property_show === 1 || b.is_property_show === 'true' || b.is_property_show === '1',
            is_project_show: b.is_project_show === true || b.is_project_show === 1 || b.is_project_show === 'true' || b.is_project_show === '1',
          }));
          // Do not force-inject company_profile as visible — respect Web Config toggles.
          if (!blocks.some((b) => b.block_key === 'company_profile')) {
            blocks.push({
              id: 11,
              block_name: 'Company Profile',
              block_key: 'company_profile',
              block_icon: 'company',
              is_property_show: false,
              is_project_show: false,
              block_type: 'company_profile',
            });
          }
          setPropertyBlocks(blocks);
        }
      }).catch(() => {});
      API.get('/project-blocks', loc).then(r => {
        if (r.data?.length) {
          const blocks = (Array.isArray(r.data) ? r.data : []).map((b) => ({
            ...b,
            is_property_show: b.is_property_show === true || b.is_property_show === 1 || b.is_property_show === 'true' || b.is_property_show === '1',
            is_project_show: b.is_project_show === true || b.is_project_show === 1 || b.is_project_show === 'true' || b.is_project_show === '1',
          }));
          setProjectBlocks(blocks);
        }
      }).catch(() => {});
      API.get('/login-input', loc).then(r => { const d = r.data; if (d?.login?.length || d?.signup?.length) setLogininput(d); }).catch(() => {});
      API.get('/property-inputs', loc).then(async (r) => {
        const d = r.data;
        if (Array.isArray(d) && d.length) {
          setPropertyInputs(arrToObj(d));
        } else {
          const merged = await mergeContentTranslations(API, 'property-inputs', locale, arrToObj(_propertyInputs), { keyedByField: true });
          setPropertyInputs(merged);
        }
      }).catch(async () => {
        setPropertyInputs(await mergeContentTranslations(API, 'property-inputs', locale, arrToObj(_propertyInputs), { keyedByField: true }));
      });
      API.get('/project-inputs', loc).then(async (r) => {
        const d = r.data;
        if (Array.isArray(d) && d.length) {
          setProjectInputs(arrToObj(d));
        } else {
          const merged = await mergeContentTranslations(API, 'project-inputs', locale, arrToObj(_projectInputs), { keyedByField: true });
          setProjectInputs(merged);
        }
      }).catch(async () => {
        setProjectInputs(await mergeContentTranslations(API, 'project-inputs', locale, arrToObj(_projectInputs), { keyedByField: true }));
      });
      API.get('/booking-form', loc).then(r => { if (r.data?.length) setBookingform(r.data); }).catch(() => {});
      API.get('/content/privacy', loc).then(r => {
        const payload = r.data;
        if (payload && typeof payload === 'object' && Object.keys(payload).length) setPrivacy(payload);
      }).catch(() => {});
      API.get('/content/terms', loc).then(r => {
        const payload = r.data;
        if (payload && typeof payload === 'object' && Object.keys(payload).length) setTermsData(payload);
      }).catch(() => {});
    }, 800);

    tier1();
    return () => { clearTimeout(tier2); clearTimeout(tier3); };
  }, [locale]);

  // ── Filters ───────────────────────────────────────────────
  const defaultFilters = {
    listing_kind: 'all', listing_type: 'all', property_type: 'all',
    category_id: [], bhk: [], amenities_id: [], sales_type: [], direction: [],
    pmin: 0, pmax: 100000000, lamin: 0, lamax: 100000,
    state_id: null, city_id: null, sort: null, search: '',
  };
  const [filters, setFilters] = useState(defaultFilters);

  const urlMapping = {
    listing_kind: 'kind',  listing_type: 'type',  property_type: 'ptype',
    category_id:  'cat',   bhk: 'bhk',             amenities_id: 'am',
    sales_type:   'sale',  direction: 'dir',        pmin: 'pmin',
    pmax:         'pmax',  lamin: 'lamin',          lamax: 'lamax',
    state_id:     'state', city_id: 'city',         sort: 'sort',
    search:       'search',
  };

  useEffect(() => {
    const next = { ...defaultFilters };
    Object.entries(urlMapping).forEach(([sk, uk]) => {
      if (!searchParams.has(uk)) return;
      const val = searchParams.get(uk);
      if (Array.isArray(defaultFilters[sk]))        next[sk] = val.split(',');
      else if (typeof defaultFilters[sk] === 'number') next[sk] = parseInt(val);
      else next[sk] = val;
    });
    setFilters(next);
  }, [searchParams]); // eslint-disable-line

  const updateUrl = useCallback((nf) => {
    const params = new URLSearchParams();
    Object.entries(nf).forEach(([sk, val]) => {
      const uk  = urlMapping[sk]; if (!uk) return;
      const def = val === defaultFilters[sk] ||
        (Array.isArray(val) && !val.length) ||
        val === null || val === '';
      if (!def) params.set(uk, Array.isArray(val) ? val.join(',') : String(val));
    });
    setSearchParams(params, { replace: true });
  }, [setSearchParams]); // eslint-disable-line

  const applyRange  = (u) => updateUrl({ ...filters, ...u });
  const clearFilters = ()  => setSearchParams({}, { replace: true });

  const filteredProperties = useMemo(() => {
    let r = [...properties];
    r = r.filter(p => {
      if (filters.listing_kind === 'property' && p.is_project)  return false;
      if (filters.listing_kind === 'project'  && !p.is_project) return false;
      if (filters.listing_type !== 'all' && p.listing_type !== filters.listing_type) return false;
      if (filters.property_type !== 'all') {
        const matchType = propertyTypes.find(t => t.type_name.toLowerCase() === filters.property_type);
        if (matchType) {
          const cats = categorys.filter(c =>
            Array.isArray(c.type_id) ? c.type_id.includes(matchType.id) : c.type_id === matchType.id
          ).map(c => String(c.id));
          if (!cats.includes(String(p.category_id))) return false;
        }
      }
      if (filters.category_id.length && !filters.category_id.includes(String(p.category_id))) return false;
      if (filters.amenities_id.length) {
        const pAm = (p.amenities_id || []).map(String);
        if (!filters.amenities_id.every(id => pAm.includes(id))) return false;
      }
      if (filters.bhk.length && !filters.bhk.includes(String(p.bhk))) return false;
      if (filters.state_id) {
        const vc = cities.filter(c => String(c.state_id) === String(filters.state_id)).map(c => String(c.id));
        if (!vc.includes(String(p.city_id))) return false;
      }
      if (filters.city_id && String(p.city_id) !== String(filters.city_id)) return false;
      if (filters.search) {
        const s = filters.search.toLowerCase();
        if (!p.location?.toLowerCase().includes(s) && !p.property_id?.toString().includes(s)) return false;
      }
      if (p.price < filters.pmin || p.price > filters.pmax) return false;
      const area = p.buildup_area || p.plot_area || p.land_area || 0;
      if (area < filters.lamin || area > filters.lamax) return false;
      return true;
    });
    if (filters.sort === 'price_asc')  r.sort((a, b) => a.price - b.price);
    if (filters.sort === 'price_desc') r.sort((a, b) => b.price - a.price);
    if (filters.sort === 'newest')     r.sort((a, b) => new Date(b.date) - new Date(a.date));
    return r;
  }, [filters, properties, propertyTypes, categorys, cities]);

  // ── buildPropertyPayload ──────────────────────────────────
  const buildPropertyPayload = useCallback(async (fd, role, listtype, isDraft = false) => {

    // ── 1. Thumbnail + Slider ────────────────────────────
    let thumbnailPath = '';
    let sliderPaths   = [];
    try {
      if (fd.thumbnail instanceof File)
        thumbnailPath = await uploadFile(fd.thumbnail);
      else if (typeof fd.thumbnail === 'string' && fd.thumbnail)
        thumbnailPath = fd.thumbnail;
      if (fd.slider?.length) sliderPaths = await uploadFiles(fd.slider);
    } catch (e) { console.error('Upload error:', e.message); }

    // ── 2. Documents — layout & brochure ✅ ──────────────
    const layoutType   = fd.layout_type   || 'image';
    const brochureType = fd.brochure_type || 'image';
    let layoutPaths   = layoutType   === 'image' ? [] : null;
    let brochurePaths = brochureType === 'image' ? [] : null;
    try {
      layoutPaths   = await uploadDocument(fd.layout,   layoutType);
      brochurePaths = await uploadDocument(fd.brochure, brochureType);
    } catch (e) { console.error('Document upload error:', e.message); }

    // ── 3. Company logo ✅ ───────────────────────────────
    let companyLogoPath = fd.company_logo || null;
    try {
      if (fd.company_logo instanceof File)
        companyLogoPath = await uploadFile(fd.company_logo);
    } catch (e) { console.error('Company logo upload error:', e.message); }

    // ── 4. Video thumbnails ──────────────────────────────
    const videos = fd.videos || [];
    const uploadedVideos = await Promise.all(videos.map(async (v) => {
      let thumbPath = null;
      if (v.thumbnail instanceof File) {
        try { thumbPath = await uploadFile(v.thumbnail); }
        catch (e) { console.error('Video thumb upload error:', e); }
      } else if (typeof v.thumbnail === 'string' && v.thumbnail) {
        thumbPath = v.thumbnail;
      }
      return { ...v, thumbnail: thumbPath };
    }));
    const youtube_links = uploadedVideos.map(v => v.video_link).filter(Boolean);
    const shorts_links  = (fd.shorts || []).map(s => s.short_link).filter(Boolean);
    const videos_data   = uploadedVideos
      .filter(v => v.video_link)
      .map(v => ({ video_link: v.video_link, thumbnail: v.thumbnail || null }));

    // ── 5. Property plans ────────────────────────────────
    const rawPlans = fd.property_plan || [];
    const uploadedPlans = await Promise.all(rawPlans.map(async (p) => {
      let imgPath = null;
      if (p.image instanceof File) {
        try { imgPath = await uploadFile(p.image); }
        catch (e) { console.error('Plan upload error:', e); }
      } else if (typeof p.image === 'string' && p.image) {
        imgPath = p.image;
      }
      return { title: p.title || '', description: p.description || '', image: imgPath };
    }));

    // ── 6. Amenities split ───────────────────────────────
    const allAmenities    = fd.aminities || fd.amenities_id || [];
    const amenities_id    = allAmenities
      .filter(v => (typeof v === 'number') || (typeof v === 'string' && v.trim() && !isNaN(Number(v))))
      .map(Number);
    const custom_amenities = allAmenities
      .filter(v => typeof v === 'string' && v.trim() && isNaN(Number(v)));

    // ── 7. Nearest + Additional ──────────────────────────
    const nearest_location = (fd.nearest_location || []).filter(n => n.near_id)
      .map(n => ({ near_id: Number(n.near_id), distance: String(n.distance || '') }));
    const additional_info  = (fd.additional_info || []).filter(a => a.info_key)
      .map(a => ({ info_key: String(a.info_key), info_value: String(a.info_value || '') }));

    // ✅ Detect Admin/Partner from route + localStorage (never mix with loggedUser on staff routes)
    let adminId = null;
    let partnerIdArr = [];
    let detectedRole = 'user'; // default to user
    let isPartnerRoute = false;
    let isAdminRoute = false;

    try {
      isPartnerRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/partner');
      isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

      if (isPartnerRoute) {
        const rawPartner = localStorage.getItem('partnerUser');
        if (rawPartner) {
          const partnerObj = JSON.parse(rawPartner);
          partnerIdArr = [toNum(partnerObj.id)];
          detectedRole = 'partner';
        }
      } else if (isAdminRoute) {
        const rawAdmin = localStorage.getItem('adminUser');
        if (rawAdmin) {
          const adminObj = JSON.parse(rawAdmin);
          // Prefer numeric account id (never ObjectId / NaN) so My Listings can scope per admin
          const candidates = [adminObj.id, adminObj.admin_id]
            .map((v) => toNum(v))
            .filter((n) => Number.isFinite(n) && n > 0);
          adminId = candidates[0] || null;
          detectedRole = adminObj.role || 'admin';
        }
      }
    } catch {}

    const userRole = fd.role || role || detectedRole;
    const staffRole = ['admin', 'sub_admin', 'partner'].includes(String(userRole || '').toLowerCase());
    const isStaffCreate = isPartnerRoute || isAdminRoute || staffRole;

    // ── 8. User ID — only for end-user listings (Owner/Agent).
    // When Admin/Partner are also logged in the same browser, never stamp loggedUser.id
    // onto staff listings (that leaked staff properties into User → My Listings).
    let userId = null;
    if (!isStaffCreate) {
      userId = toNum(fd.user_id);
      if (!userId) {
        try {
          const raw = localStorage.getItem('loggedUser');
          if (raw) userId = toNum(JSON.parse(raw).id);
        } catch {}
      }
    }
    
    // Prefer form data selection (set by Rent/Sale buttons), fall back to URL param, then default
    const rawListingType = fd.listing_type || listtype || 'Sale';
    // Normalize to proper case: 'sale' → 'Sale', 'rent' → 'Rent'
    const listing_type = rawListingType.charAt(0).toUpperCase() + rawListingType.slice(1).toLowerCase();

    // ── 9. Extra fields — all non-fixed keys ─────────────
    const FIXED_FIELDS = new Set([
      'type_id', 'category_id', 'city_id', 'state_id', 'user_id', 'role', 'price',
      'booking', 'is_draft', 'is_urgent', 'is_highlighted', 'is_dtcp', 'is_project',
      'parking', 'corner_plot', 'slider_show', 'slider_work',
      'location', 'address', 'address_details', 'map_location_name', 'latitude', 'longitude',
      'bhk', 'direction', 'furnishing', 'property_availability', 'property_floor_no',
      'total_floors', 'gated_community', 'plot_length', 'plot_breadth',
      'land_area_cent', 'land_area', 'plot_area', 'length', 'buildup_area',
      'amenities_id', 'partner_id', 'sub_admin_id', 'youtube_links', 'shorts_links',
      'thumbnail', 'slider', 'nearest_location', 'additional_info', 'property_plan',
      'seo_title', 'seo_description', 'sales_type', 'listing_type', 'status_id', 'date',
      'aminities', 'videos', 'shorts', 'enable_video_section', 'enable_plan_section',
      'custom_amenities', 'id', 'property_id', 'createdAt', 'updatedAt', 'extra_fields',
      // ✅ Range fields
      'price_from', 'price_to',
      'total_area_from', 'total_area_to', 'total_area_unit',
      'bhk_from', 'bhk_to',
      // ✅ Documents
      'layout_type', 'layout', 'brochure_type', 'brochure',
      // ✅ Company profile
      'company_logo', 'company_name', 'company_description', 'company_id', 'company_is_new',
      // Staff-only owner note (Admin/Partner)
      'property_owner_name', 'property_owner_number',
      'property_owner_number_added_by_role', 'property_owner_number_added_by_name',
      'property_owner_number_added_by_id', 'property_owner_number_added_at',
      '_prev_property_owner_number',
      'is_own_property', 'admin_id', 'sub_register_id', 'property_age',
    ]);
    const extra_fields = {};
    Object.entries(fd).forEach(([k, v]) => {
      if (!FIXED_FIELDS.has(k) && v !== undefined && v !== '' && v !== null)
        extra_fields[k] = v;
    });

    const customParsed = saveCustomDropdownsToExtraFields(fd, extra_fields, toNum);

    // ── 10. Final payload ────────────────────────────────
    return {
      type_id:      toNum(fd.type_id),
      category_id:  toNum(fd.category_id),
      city_id:      toNum(fd.city_id),
      state_id:     toNum(fd.state_id),
      user_id:      isStaffCreate ? null : userId,
      role:         userRole,
      price:        toNum(fd.price) || 0,
      booking:      Boolean(fd.booking),
      is_draft:     Boolean(isDraft),
      is_urgent:    Boolean(fd.is_urgent),
      is_highlighted: Boolean(fd.is_highlighted),
      is_dtcp:      Boolean(fd.is_dtcp),
      is_project:   Boolean(fd.is_project),
      is_own_property: Boolean(fd.is_own_property),
      parking:      Boolean(fd.parking),
      corner_plot:  Boolean(fd.corner_plot),
      slider_show:  Boolean(fd.slider_show),
      slider_work:  Boolean(fd.slider_work),
      location:           fd.location || fd.map_location_name || fd.address || '',
      address:            fd.address || '',
      address_details:    fd.address_details || '',
      map_location_name:  fd.map_location_name || '',
      latitude:           toNum(fd.latitude),
      longitude:          toNum(fd.longitude),
      bhk:                customParsed.bhk,
      direction:          toNum(fd.direction),
      furnishing:         toNum(fd.furnishing),
      property_availability: toNum(fd.property_availability),
      property_floor_no:  customParsed.property_floor_no,
      total_floors:       customParsed.total_floors,
      gated_community:    toNum(fd.gated_community),
      amenities_id,
      custom_amenities,
      plot_length:        toNum(fd.plot_length),
      plot_breadth:       toNum(fd.plot_breadth),
      land_area_cent:     toNum(fd.land_area_cent),
      land_area:          toNum(fd.land_area),
      plot_area:          toNum(fd.plot_area),
      length:             toNum(fd.length),
      buildup_area:       toNum(fd.buildup_area),
      sales_type:         fd.sales_type || listing_type,
      listing_type,
      date:               fd.date || new Date().toISOString().split('T')[0],
      seo_title:          fd.seo_title || '',
      seo_description:    fd.seo_description || '',
      thumbnail:          thumbnailPath,
      slider:             sliderPaths,
      youtube_links,
      shorts_links,
      videos:             videos_data,
      nearest_location,
      additional_info,
      property_plan:      uploadedPlans,
      partner_id:         partnerIdArr.length ? partnerIdArr : (fd.partner_id  || []).map(Number).filter(Boolean),
      admin_id:           adminId || toNum(fd.admin_id),
      sub_admin_id:       (fd.sub_admin_id|| []).map(Number).filter(Boolean),
      status_id:          isDraft
        ? 0
        : (fd.status_id !== undefined && fd.status_id !== null && fd.status_id !== ''
          ? Number(fd.status_id)
          : 1),
      extra_fields,

      // ✅ Range fields
      price_from:         toNum(fd.price_from),
      price_to:           toNum(fd.price_to),
      total_area_from:    toNum(fd.total_area_from),
      total_area_to:      toNum(fd.total_area_to),
      total_area_unit:    toNum(fd.total_area_unit),
      bhk_from:           toNum(fd.bhk_from),
      bhk_to:             toNum(fd.bhk_to),

      // ✅ Documents
      layout_type:        fd.layout_type   || 'image',
      layout:             layoutPaths,
      brochure_type:      fd.brochure_type || 'image',
      brochure:           brochurePaths,

      // ✅ Company Profile
      company_id:          fd.company_id != null && fd.company_id !== '' ? Number(fd.company_id) : null,
      company_logo:        companyLogoPath,
      company_name:        fd.company_name        || '',
      company_description: fd.company_description || '',
      company_is_new:      !!fd.company_is_new && !fd.company_id,

      // Staff-only — Admin/Partner Property Owner Name + Number
      ...(() => {
        const ownerName = String(fd.property_owner_name || '').trim();
        const ownerNumber = String(fd.property_owner_number || '').trim();
        const out = {
          property_owner_name: ownerName,
          property_owner_number: ownerNumber,
        };
        if (!ownerNumber) {
          out.property_owner_number_added_by_role = '';
          out.property_owner_number_added_by_name = '';
          out.property_owner_number_added_by_id = null;
          out.property_owner_number_added_at = null;
          return out;
        }
        // Keep existing attribution unless number changed or attribution missing
        const prevNumber = String(fd._prev_property_owner_number || '').trim();
        const hasAttribution = !!(fd.property_owner_number_added_by_name || fd.property_owner_number_added_by_role);
        if (ownerNumber === prevNumber && hasAttribution) {
          out.property_owner_number_added_by_role = fd.property_owner_number_added_by_role || '';
          out.property_owner_number_added_by_name = fd.property_owner_number_added_by_name || '';
          out.property_owner_number_added_by_id = fd.property_owner_number_added_by_id ?? null;
          out.property_owner_number_added_at = fd.property_owner_number_added_at || null;
          return out;
        }
        try {
          const isPartner = typeof window !== 'undefined' && window.location.pathname.startsWith('/partner');
          const raw = localStorage.getItem(isPartner ? 'partnerUser' : 'adminUser');
          const staff = raw ? JSON.parse(raw) : null;
          out.property_owner_number_added_by_role = isPartner
            ? 'partner'
            : (staff?.role || 'admin');
          out.property_owner_number_added_by_name = isPartner
            ? (staff?.name || 'Partner')
            : (staff?.admin_name || staff?.name || 'Admin');
          out.property_owner_number_added_by_id = Number(staff?.id || staff?.admin_id) || null;
          out.property_owner_number_added_at = new Date().toISOString();
        } catch {
          out.property_owner_number_added_by_role = detectedRole || 'admin';
          out.property_owner_number_added_by_name = 'Staff';
          out.property_owner_number_added_by_id = null;
          out.property_owner_number_added_at = new Date().toISOString();
        }
        return out;
      })(),
    };
  }, []);

  // ── CRUD ──────────────────────────────────────────────────
const createProperty = useCallback(async (fd, role, listtype) => {
  try {
    setLoading(true);
    const payload = await buildPropertyPayload(fd, role, listtype, false);
    const res     = await API.post('/property', payload);
    fetchProperties();
    return res.data;
  } finally {
    setLoading(false);
  }
}, [buildPropertyPayload, fetchProperties]);

const updateProperty = useCallback(async (id, fd, role, listtype) => {
  try {
    setLoading(true);
    const payload = await buildPropertyPayload(fd, role, listtype, false);
    const res     = await API.put(`/property/${id}`, payload);
    fetchProperties();
    return res.data;
  } finally {
    setLoading(false);
  }
}, [buildPropertyPayload, fetchProperties]);





  const deleteProperty = useCallback(async (id) => {
    await API.delete(`/property/${id}`);
    fetchProperties();
  }, [fetchProperties]);

  const saveDraftToBackend = useCallback(async (fd, role, listtype) => {
    try {
      const payload = await buildPropertyPayload(fd, role, listtype, true);
      const res = fd.id
        ? await API.put(`/property/${fd.id}`, payload)
        : await API.post('/property', payload);
      return res.data;
    } catch (e) {
      console.log('saveDraft error:', e.message);
      return null;
    }
  }, [buildPropertyPayload]);

  // ── Context value ─────────────────────────────────────────
  return (
    <MyContext.Provider value={{
      user, setUser, isAuth, wishlist, toggleWishlist,
      properties, propLoading, fetchProperties, fetchPropertiesPaged, fetchPropertyById, dropdown, loading,
      createProperty, updateProperty, deleteProperty, saveDraftToBackend,
      selectedCity, changeCity,
      selectedPropertyType, selectedCategory, selectedState,
      preferencesComplete, savePreferences,
      locale, changeLocale,
      localizationEnabled,
      appDefaultLocale,
      refreshLocalizationSettings,

      aminities, adsData,
      aminitiesCategories,
      nearestlocation, landtype, states, subregisters,
      listingTypes, roles,
      propertyBlocks, projectBlocks,
      propertydetail,
      colors, BASE_URL,
      imgUrl: getImgUrl,

      // ✅ Dynamic inputs (object keyed by field)
      propertyInputs,
      projectInputs,

      admins,
      bookingform,
      categorys,
      cities,
      contact,
      footerData,
      heroData:     heroData     || {},
      nearLocationConfig: nearLocationConfig || NEAR_LOCATION_DEFAULTS,
      refreshNearLocationConfig,
      refreshHeroConfig,
      propertyListPageConfig,
      refreshPropertyListPageConfig,
      homeSections,
      logininput,
      users:        Array.isArray(_users) ? _users : [],
      whyChooseData,
      youtubeShorts,

      // ✅ Dynamic from backend
      partners, socialMedia, navLinks,
      privacy, termsData,
      filterConfig,
      PROJECT_TYPE_SHOW: projectTypeShow,
      propertyTypes,
      projectTypes,

      filters, applyRange, clearFilters, filteredProperties,
    }}>
      {children}
    </MyContext.Provider>
  );
};

export default MyProvider;